import { useEffect, useRef, useCallback } from 'react';
import { AlertState, getAccentColor } from '../../context/AlertContext';
import styles from './AlertModal.module.scss';

interface AlertModalProps {
  state: AlertState;
  onClose: (result: boolean) => void;
}

const FOCUSABLE_SELECTOR =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

export default function AlertModal({ state, onClose }: AlertModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const primaryButtonRef = useRef<HTMLButtonElement>(null);

  // Store the previously-focused element before modal renders
  useEffect(() => {
    previousFocusRef.current = document.activeElement as HTMLElement | null;

    return () => {
      // Return focus on unmount
      previousFocusRef.current?.focus();
    };
  }, []);

  // Move focus to primary action button on mount
  useEffect(() => {
    primaryButtonRef.current?.focus();
  }, []);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose(false);
        return;
      }

      if (e.key === 'Tab') {
        const modal = modalRef.current;
        if (!modal) return;

        const focusableElements = Array.from(
          modal.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
        );
        if (focusableElements.length === 0) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }
    },
    [onClose]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose(false);
    }
  };

  const accentColor = getAccentColor(state.type);
  const hasTitle = Boolean(state.title);

  return (
    <div className={styles.backdrop} onClick={handleBackdropClick}>
      <div
        ref={modalRef}
        className={styles.modal}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={hasTitle ? 'alert-modal-title' : undefined}
        aria-label={!hasTitle ? state.message : undefined}
        aria-describedby="alert-modal-message"
        style={{ '--alert-accent': accentColor } as React.CSSProperties}
      >
        {hasTitle && (
          <h2 id="alert-modal-title" className={styles.title}>
            {state.title}
          </h2>
        )}

        <p id="alert-modal-message" className={styles.message}>
          {state.message}
        </p>

        <div className={styles.actions}>
          {state.mode === 'confirm' && (
            <button
              type="button"
              className={styles.cancelBtn}
              onClick={() => onClose(false)}
            >
              {state.cancelText}
            </button>
          )}
          <button
            ref={primaryButtonRef}
            type="button"
            className={styles.confirmBtn}
            onClick={() => onClose(true)}
          >
            {state.confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}
